import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { composeApiURL } from "helpers/utility/axiosHelpers";
import { useGetStats } from "./useGetStats";
import { useUserConfig } from "../useUserConfig";

export enum FlowStatus {
  INIT = "INIT",
  RUNNING = "RUNNING",
  REVIEW = "REVIEW",
  FAILED = "FAILED",
  PAUSED_FOR_REVIEW = "PAUSED_FOR_REVIEW",
  COMPLETED = "COMPLETED",
  RESUME_FLOW = "RESUME_FLOW",
  APPLY_MODEL_TO_DATASET = "APPLY_MODEL_TO_DATASET",
}

type FlowStatusResponse = {
  status: FlowStatus;
};

type Props = {
  datasetId: string;
  flowId: string | null;
  seedId: string | null;
  active: boolean;
  avoidPollStats?: boolean;
};

const INITIAL_RETRY_DELAY = 5000;
const MAX_RETRY_DELAY = 30000;

export const usePollSeedStatusAndStats = ({
  datasetId,
  flowId,
  seedId,
  active,
  avoidPollStats,
}: Props) => {
  const [status, setStatus] = useState<FlowStatus | null>(null);
  const [polling, setPolling] = useState<boolean>(false);
  const [error, setError] = useState<any>(null);

  const etagRef = useRef<string | null>(null);

  const { stats, fetchStats } = useGetStats();
  const { refetch: refetchUserConfig } = useUserConfig();

  useEffect(() => {
    if (!active) {
      setStatus(null);
    }
  }, [active]);

  const HOST_URL = process.env.REACT_APP_API_ENDPOINT!;

  const isFinalStatus = (s: FlowStatus) =>
    s === FlowStatus.REVIEW ||
    s === FlowStatus.FAILED ||
    s === FlowStatus.PAUSED_FOR_REVIEW ||
    s === FlowStatus.COMPLETED;

  useEffect(() => {
    if (!active || !flowId) return;

    const abortController = new AbortController();
    let stopped = false;

    const poll = async () => {
      setPolling(true);
      let retryDelay = INITIAL_RETRY_DELAY;

      while (!stopped) {
        try {
          const params: Record<string, string | boolean> = {};
          if (etagRef.current) {
            params.wait = true;
            params.etag = etagRef.current;
          }

          const statusUrl = composeApiURL(
            HOST_URL,
            `/flywheel/${datasetId}/status/${flowId}`,
          );

          const res = await axios.get<FlowStatusResponse>(statusUrl, {
            params,
            signal: abortController.signal,
            validateStatus: (s) => s === 200 || s === 304,
          });

          if (res.status === 304) {
            continue;
          }

          const newEtag = res.headers["x-etag"];
          if (newEtag) {
            etagRef.current = newEtag;
          }

          const currentStatus = res.data.status;
          setStatus(currentStatus);

          if (currentStatus !== FlowStatus.INIT && !avoidPollStats) {
            await fetchStats(datasetId, flowId);
          }

          retryDelay = INITIAL_RETRY_DELAY;

          if (isFinalStatus(currentStatus)) {
            if (currentStatus === FlowStatus.COMPLETED) {
              refetchUserConfig();
            }
            break;
          }
        } catch (err) {
          if (axios.isCancel(err) || stopped) {
            break;
          }
          setError(err);
          const delay = retryDelay;
          await new Promise((r) => setTimeout(r, delay));
          retryDelay = Math.min(retryDelay * 2, MAX_RETRY_DELAY);
        }
      }

      setPolling(false);
    };

    poll();

    return () => {
      stopped = true;
      abortController.abort();
      etagRef.current = null;
    };
    // deliberately not adding fetchStats/refetchUserConfig to deps to avoid infinite re-run
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, datasetId, flowId, seedId, HOST_URL]);

  return {
    status,
    stats,
    error,
    isFinalStatus,
    polling,
  };
};
